import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as FileSaver from 'file-saver';
import { UserSettingService } from '../../../account/services';
import { Company, User } from '../../../auth/models';
import { UserSettings } from '../../../auth/models/user/user-settings';
import { ToastService } from '../../../main/services';
import { InfoModalComponent } from "../../components";
import { ExportTemplatePickerModalComponent } from '../../components/export-template-picker-modal/export-template-picker-modal.component';
import { Tender } from '../../models';
import { TENDER_STATUSES } from '../../models/tender';
import {
    ProposalService,
    SupplierBidService,
    TenderBidXlsImportService,
    TenderContractSuppliersService,
    TenderService, TenderXmlExportsService
} from '../../services';
import { SealedTenderService } from '../../services/sealed-tender/sealed-tender.service';
import { TenderBidImportModalComponent } from './components/tender-bid-import-modal/tender-bid-import-modal.component';

@Component({
    selector: 'jft-tender',
    templateUrl: './tender.component.html',
    styleUrls: ['./tender.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderComponent implements OnInit {

    public tender: Tender;
    public isCollapsed: boolean = false;
    // Mobile-only: expands the tender action items (Excel Import / Export / Cancel).
    public actionsOpen: boolean = false;
    public TENDER_STATUSES = TENDER_STATUSES;
    public loadingExport: boolean = false;
    public preferredCurrencyCode: string = '';

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private tenderService: TenderService,
        private bidService: SupplierBidService,
        private xmlExportsService: TenderXmlExportsService,
        private tenderContractSuppliersService: TenderContractSuppliersService,
        private proposalService: ProposalService,
        private userSettingService: UserSettingService,
        private tenderBidXlsImport: TenderBidXlsImportService,
        private toast: ToastService,
        private injector: Injector,
        private ngbModal: NgbModal,
        private sealedTenderService: SealedTenderService,
        public user: User,
        public company: Company
    ) {
    }

    public ngOnInit() {
        let data = this.route.snapshot.data;
        this.tender = data['tender'];

        this.bidService.onBidSubmitted.subscribe(() => this.update());
        this.proposalService.onBidSubmitted.subscribe(() => this.update());
        this.tenderContractSuppliersService.onAwarded.subscribe(() => this.update());

        this.isCollapsed = this.user.settings.tender_stats;

        // Use the tender's currency as the preferred currency for now
        if (this.tender && this.tender.currency) {
            this.preferredCurrencyCode = this.tender.currency.code;
        }
    }

    public update() {
        this.tenderService.fetch(this.tender.id).subscribe(data => this.tender = data)
    }

    public cancelTender() {
        this.tenderService.cancel(this.tender)
            .subscribe((tender: Tender) => {
                this.tender = tender;
                this.router.navigateByUrl('/tenders');
                this.toast.success(`Tender: ${tender.name} cancelled`);
            }, (e) => {
                this.toast.error(e);
            });
    }

    /** D-435: a draft is deleted, never cancelled. */
    public isDraft(): boolean {
        return this.tender?.status === TENDER_STATUSES.DRAFT;
    }

    public deleteDraft() {
        this.tenderService.delete(this.tender).subscribe(() => {
            this.router.navigateByUrl('/tenders');
            this.toast.success('The draft is deleted.');
        }, (e) => {
            this.toast.error(e?.status === 409
                ? 'This tender is launched. It can no longer be deleted.'
                : 'The draft was not deleted. Try again in a moment.');
        });
    }

    public deleteTender() {
        this.tenderService.deleteImported(this.tender).subscribe(() => {
            this.router.navigateByUrl('/tenders');
                this.toast.success(`Tender: ${this.tender.name} has been deleted`);
        }, (e) => {
            this.toast.error(e);
        });
    }

    public exportTender($event: Event, isLocalUnits: boolean = false) {
        $event.preventDefault();
        $event.stopPropagation();
        let location = '';
        this.loadingExport = true;
        this.tenderService.exportTender(this.tender, undefined, isLocalUnits).subscribe(result => {
            this.loadingExport = false;
            if (result.type === 'task') {
                this.toast.success(
                    'Export will be ready for download in the notification section',
                    'Requested Export',
                );
                return;
            }
            FileSaver.saveAs(result.blob, `${this.tender.name}${location}-tender-bids.xlsx`);
        }, error => {
            this.loadingExport = false;
        });
    }

    /**
     * Check if export functionality should be available for sealed tenders
     */
    public canExportBidData(): boolean {
        if (!this.company || !this.tender) {
            return true; // Default to allowing exports if no data
        }

        // For sealed tenders, check if any rounds are still active
        if (this.sealedTenderService.isSealedTender(this.company)) {
            // Use service method to check if aggregate data can be accessed
            return this.sealedTenderService.canAccessAggregateData(this.company, this.tender);
        }

        return true; // Allow exports for realtime tenders
    }

    /**
     * Check if this is a sealed tender
     */
    public isSealedTender(): boolean {
        return this.sealedTenderService.isSealedTender(this.company);
    }

    /**
     * Check if tender overview access is allowed
     */
    public canAccessTenderOverview(): boolean {
        if (!this.company || !this.tender) {
            return true; // Default to allowing access if no data
        }

        // For sealed tenders, check if any rounds are still active
        if (this.sealedTenderService.isSealedTender(this.company)) {
            // For overview pages, all rounds must be completed
            return this.sealedTenderService.canAccessAggregateData(this.company, this.tender);
        }

        return true; // Allow access for non-sealed tenders
    }

    /**
     * Check if locations access is allowed
     */
    public canAccessLocations(): boolean {
        if (!this.company || !this.tender) {
            return true; // Default to allowing access if no data
        }

        // For sealed tenders, check if any rounds are still active
        if (this.sealedTenderService.isSealedTender(this.company)) {
            // For locations pages, all rounds must be completed
            return this.sealedTenderService.canAccessAggregateData(this.company, this.tender);
        }

        return true; // Allow access for non-sealed tenders
    }



    /**
     * Get tooltip message for tender overview restriction
     */
    public getTenderOverviewRestrictionTooltip(): string {
        return 'Tender overview is not available for sealed tenders during active rounds.';
    }

    /**
     * Get tooltip message for locations restriction
     */
    public getLocationsRestrictionTooltip(): string {
        return 'Locations are not available for sealed tenders during active rounds.';
    }

    /**
     * Check if awards access is allowed
     */
    public canAccessAwards(): boolean {
        if (!this.company || !this.tender) {
            return true; // Default to allowing access if no data
        }

        // For sealed tenders, check if any rounds are still active
        if (this.sealedTenderService.isSealedTender(this.company)) {
            return this.sealedTenderService.canAccessAggregateData(this.company, this.tender);
        }

        return true; // Allow access for non-sealed tenders
    }

    /**
     * Get tooltip message for awards restriction
     */
    public getAwardsRestrictionTooltip(): string {
        return 'Awards are not available for sealed tenders during active rounds.';
    }

    /**
     * Get tooltip message for export restriction
     */
    public getExportRestrictionTooltip(): string {
        return 'Export and analysis features are not available for sealed tenders during active rounds.';
    }



    // supplier and airline values - now uses async background export
    public exportTenderOverviewAll($event: Event, isLocalUnits: boolean = false) {
        $event.preventDefault();
        $event.stopPropagation();

        this.tenderService.exportTenderOverviewAllAsync(this.tender).subscribe({
            next: (response) => {
                this.toast.info('Export started. You\'ll be notified when the file is ready for download.');
            },
            error: (error) => {
                this.toast.error('Failed to start export. Please try again.');
            }
        });
    }

    public exportTenderInvitationXml($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.xmlExportsService.fetchInvitation(this.tender.id)
    }

    public exportTenderUpdateXml($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.xmlExportsService.fetchTenderUpdate(this.tender.id)
    }

    public exportTenderBidAwardXml($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.xmlExportsService.fetchBidAward(this.tender.id)
    }

    public exportTenderBidDeclineXml($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.xmlExportsService.fetchBidDecline(this.tender.id)
    }

    public exportTenderCancelXml($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.xmlExportsService.fetchTenderCancel(this.tender.id)
    }

    public exportNewRoundXml($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.xmlExportsService.fetchNewRound(this.tender.id);
    }

    public openImportBidXlsModalContacts() {
        this.ngbModal.open(TenderBidImportModalComponent, {
            injector: Injector.create([
                {provide: Tender, useValue: this.tender},
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true,
        }).result.then((item) => {
            if (item) {
                this.toast.success(item);
            }
        }, (error) => {
            // Dismissed - no secondary modal needed
        });
    }

    public openInfoModal(message) {
        this.ngbModal.open(InfoModalComponent, {
            injector: Injector.create([
                {provide: Tender, useValue: message},
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true,
            windowClass: 'invitation-info-modal'
        }).result.then((data) => {
            //
        }, (error) => {
        });
    }

    public collapseStats() {
        this.isCollapsed = !this.isCollapsed;

        this.userSettingService.update({tender_stats: this.isCollapsed})
            .subscribe((data: UserSettings) => {
                //this.isCollapsed = data.tenderStats;
                this.user.settings.tender_stats = data.tender_stats;
            }, (e) => {
                this.toast.error(e);
            });
    }

    public openExportTemplatePicker($event: Event): void {
        $event.preventDefault();
        $event.stopPropagation();
        const modalRef = this.ngbModal.open(ExportTemplatePickerModalComponent, {
            size: 'lg',
            centered: true
        });
        modalRef.componentInstance.tenderUid = this.tender.id;
        modalRef.componentInstance.tenderName = this.tender.name;
    }
}
